import React, { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { Backdrop, Box, CircularProgress } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { useBlocker } from 'react-router';

import presets from '@/components/Dialog/presets';
import { PageInformationContext } from '@/context/PageInformationContext';
import { RemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import AppsOrder from '@/features/Dashboard/pages/Edit/components/AppsOrder';
import { EditBanner } from '@/features/Dashboard/pages/Edit/components/EditBanner';
import EditDashboardName from '@/features/Dashboard/pages/Edit/components/EditDashboardName';
import { TilesOrder } from '@/features/Dashboard/pages/Edit/components/TilesOrder';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { useDashboard, useDeleteDashboard, useUpdateDashboard } from '@/hooks/api/dashboard';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { ConfirmationDialog, EditableTabLabel, EditActionButtons } from './components';
import { useDashboardTabs } from './hooks/useDashboardTabs';
import { buildCurrentTabPayload } from './utils/payload';
import { DashboardLayout } from '../../components/DashboardLayout';
import { getTabContent, normalizeTabEntry } from '../../lib/transformers';
import { DashboardTab, MarketApps, MarketAppWithTabs, TileBigNumber } from '../../types';

export const EditDashboard = ({
  onClose,
  dashboardConfig,
  dashboardStructure,
  tabNames = [],
  activeTab = 0
}: {
  onClose: () => void;
  dashboardConfig: MarketApps;
  dashboardStructure?: DashboardTab | null;
  tabNames?: string[];
  activeTab?: number;
}) => {
  const {
    marketTabApps,
    tiles,
    dashboardName,
    initialDashboardName,
    setDashboardName,
    currentTabId,
    setCurrentTabId,
    initializeTabDraft,
    getAllTabDrafts,
    tabDrafts,
    setTabDrafts,
    setDraftTabs
  } = useEditDashboardDraft();

  const { data: dashboards } = useDashboard();

  const currentDashboardTabs = useMemo(() => {
    return (
      dashboardStructure ??
      dashboards?.[initialDashboardName] ??
      dashboards?.[dashboardName] ??
      null
    );
  }, [dashboards, initialDashboardName, dashboardName, dashboardStructure]);

  const {
    draftTabs,
    draftTabNames,
    handleRenameTab,
    handleAddTabDraft,
    handleAddTabWithId,
    handleDeleteTab,
    handleReorderTabs,
    handleDraftTabChange,
    getDraftTabNameError,
    draftActiveTabIndex,
    hasHeaderTabNameErrors,
    hasTabOrderChanges,
    resetDraftTabs,
    getCurrentTab
  } = useDashboardTabs({
    tabNames,
    currentTabId,
    setCurrentTabId,
    activeTab,
    currentDashboardTabs
  });

  // Sync draftTabs to context
  useEffect(() => {
    setDraftTabs(draftTabs);
  }, [draftTabs, setDraftTabs]);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('edit');
  }, [setMode]);

  const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);
  const [isRemoveAllPopupOpen, setIsRemoveAllPopupOpen] = useState(false);
  const [isRemoveDashboardPopupOpen, setIsRemoveDashboardPopupOpen] = useState(false);
  const [isRemoveWidgetPopupOpen, setIsRemoveWidgetPopupOpen] = useState(false);
  const [removeWidgetCallback, setRemoveWidgetCallback] = useState<(() => void) | null>(null);
  const [hasDashboardNameErrors, setHasDashboardNameErrors] = useState(false);

  // Get current tab name for useUpdateDashboard (used for single-tab updates)
  const currentTabName = getCurrentTab()?.name ?? 'Main';

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    dashboardName,
    'Dashboard updated!',
    currentTabName,
    initialDashboardName
  );

  const { mutate: deleteDashboard, isPending: isDeletingDashboard } =
    useDeleteDashboard(initialDashboardName);

  const { initialMarketTabApps, initialTiles } = useMemo(() => {
    const nextMarketTabApps: MarketAppWithTabs[] = [];
    let nextTiles: TileBigNumber[] = [];

    for (const app of dashboardConfig) {
      if ('type' in app) {
        nextTiles = app.tiles;
      }
      if ('tabs' in app) {
        nextMarketTabApps.push(app);
      }
    }

    return {
      initialMarketTabApps: nextMarketTabApps,
      initialTiles: nextTiles
    };
  }, [dashboardConfig]);

  const hasTilesChanges = useMemo(() => {
    return !isEqual(tiles, initialTiles);
  }, [tiles, initialTiles]);

  const hasAppsChanges = useMemo(() => {
    return !isEqual(marketTabApps, initialMarketTabApps);
  }, [marketTabApps, initialMarketTabApps]);

  const appTabNameSchema = useMemo(() => createNameSchema('Name', TABNAME_MAX_LENGTH), []);

  const allTabDrafts = useMemo(() => getAllTabDrafts(), [getAllTabDrafts, tabDrafts]);

  const { hasAppTabNameErrors } = useMemo(() => {
    return draftTabs.reduce(
      (acc, draftTab) => {
        const draft = allTabDrafts[draftTab.id];
        const apps = draft?.marketTabApps ?? [];

        const tabNameError = apps.some(app =>
          app.tabs?.some(tab => !appTabNameSchema.safeParse(tab.name ?? '').success)
        );

        return {
          hasAppTabNameErrors: acc.hasAppTabNameErrors || tabNameError
        };
      },
      { hasAppTabNameErrors: false }
    );
  }, [allTabDrafts, appTabNameSchema, draftTabs]);

  // Track if we've initialized drafts for this mount
  // Since EditDashboard remounts when entering edit mode, we always want to
  // replace drafts on mount to ensure we start with current dashboard state
  const hasInitializedForMountRef = useRef(false);

  // Initialize drafts for all tabs when entering edit mode
  // Always replace drafts when entering edit mode to ensure we start with
  // current dashboard state. Use draftTabs to get stable IDs for each tab.
  // This should run before setting currentTabId
  useEffect(() => {
    // Only initialize once per mount (when entering edit mode)
    if (!hasInitializedForMountRef.current && currentDashboardTabs && draftTabs.length > 0) {
      // Clear all existing drafts when entering edit mode
      // This ensures we always start with fresh drafts from current state
      setTabDrafts({});

      // Initialize drafts for all tabs from current dashboard state
      draftTabs.forEach((tab, index) => {
        // Use sourceName to get server data; fallback to current name when sourceName is missing
        // (happens when draftTabs were created before dashboard data arrived)
        const serverTabName = tab.sourceName ?? tab.name;

        // Use dashboardConfig for active tab instead of server data
        const isActiveTab = index === activeTab;
        if (isActiveTab) {
          // Extract marketTabApps and tiles from dashboardConfig for active tab
          const tabMarketTabApps: MarketAppWithTabs[] = [];
          let tabTiles: TileBigNumber[] = [];

          for (const app of dashboardConfig) {
            if ('type' in app) {
              tabTiles = app.tiles;
            }
            if ('tabs' in app) {
              tabMarketTabApps.push(app);
            }
          }

          initializeTabDraft(tab.id, tabMarketTabApps, tabTiles);
          return;
        }

        // Check if tab exists in dashboard (new tabs won't have content yet)
        if (!serverTabName || !(serverTabName in currentDashboardTabs)) {
          // Tab doesn't exist in dashboard (new tab), initialize empty draft
          initializeTabDraft(tab.id, [], []);
          return;
        }

        const tabValue = currentDashboardTabs[serverTabName];
        const tabContent = getTabContent(tabValue);

        // Extract marketTabApps and tiles for this tab
        const tabMarketTabApps: MarketAppWithTabs[] = [];
        let tabTiles: TileBigNumber[] = [];

        for (const app of tabContent) {
          if ('type' in app) {
            tabTiles = app.tiles;
          }
          if ('tabs' in app) {
            tabMarketTabApps.push(app);
          }
        }

        initializeTabDraft(tab.id, tabMarketTabApps, tabTiles);
      });

      // Mark as initialized for this mount
      hasInitializedForMountRef.current = true;
    }
  }, [
    currentDashboardTabs,
    draftTabs,
    initializeTabDraft,
    dashboardConfig,
    activeTab,
    setTabDrafts
  ]);

  // Initialize currentTabId based on activeTab when entering edit mode
  // Should run after drafts are initialized
  useEffect(() => {
    if (draftTabs.length > 0 && !currentTabId) {
      const activeTabIndex = Math.max(0, Math.min(activeTab, draftTabs.length - 1));
      const activeTabId = draftTabs[activeTabIndex]?.id;
      if (activeTabId) {
        setCurrentTabId(activeTabId);
      }
    }
  }, [draftTabs, activeTab, currentTabId, setCurrentTabId, tabDrafts]);

  // Initialize current tab's draft (only if no draft exists yet)
  // Note: The draft loading is now handled by the context based on currentTabId
  // This effect only initializes drafts from server data when they don't exist
  // Fallback for the current tab if it wasn't initialized in the loop above
  useEffect(() => {
    if (!currentTabId) return;

    // Check if draft already exists - if so, don't overwrite user's changes
    if (!(currentTabId in tabDrafts)) {
      // No draft yet, initialize from server data
      initializeTabDraft(currentTabId, initialMarketTabApps, initialTiles);
    }
  }, [initialMarketTabApps, initialTiles, currentTabId, initializeTabDraft, tabDrafts]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  const handleRemoveAll = useCallback(() => {
    mutate([]);
  }, [mutate]);

  const handleRemoveDashboard = useCallback(() => {
    deleteDashboard();
  }, [deleteDashboard]);

  const openRemoveModal = useCallback((onOk: () => void) => {
    setRemoveWidgetCallback(() => onOk);
    setIsRemoveWidgetPopupOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDashboardName(initialDashboardName);
    resetDraftTabs(tabNames);
    onClose();
  }, [initialDashboardName, onClose, resetDraftTabs, setDashboardName, tabNames]);

  const hasTabNameErrors = hasAppTabNameErrors || hasHeaderTabNameErrors;

  const hasDraftConfigErrors = useMemo(() => {
    return hasDashboardNameErrors || hasTabNameErrors;
  }, [hasDashboardNameErrors, hasTabNameErrors]);

  const hasChanges = useMemo(() => {
    return (
      hasTilesChanges ||
      hasAppsChanges ||
      initialDashboardName !== dashboardName ||
      hasTabOrderChanges
    );
  }, [hasTilesChanges, hasAppsChanges, dashboardName, initialDashboardName, hasTabOrderChanges]);

  const handleBeforeUnload = useEffectEvent((e: BeforeUnloadEvent) => {
    if (hasChanges) {
      e.preventDefault();
      return '';
    }
  });

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const isSaveDisabled = useMemo(() => {
    return isPending || !hasChanges || hasDraftConfigErrors;
  }, [hasChanges, hasDraftConfigErrors, isPending]);

  const handleSave = useCallback(() => {
    // Get all tab drafts (includes current tab's draft)
    const allTabDrafts = getAllTabDrafts();

    // Build payload for all tabs using ID-based drafts in new format with id
    const tabsStructure: Record<string, { id: string; content: MarketApps }> = {};

    draftTabs.forEach(tab => {
      const tabName = tab.name.trim();
      let tabData: MarketApps | undefined;
      let tabId: string | undefined;

      // Get draft for this tab by ID
      if (tab.id in allTabDrafts) {
        const draft = allTabDrafts[tab.id];
        tabData = buildCurrentTabPayload(draft.tiles, draft.marketTabApps);
        // Try to preserve existing tab id if available
        if (tab.sourceName && currentDashboardTabs?.[tab.sourceName]) {
          const existingTabValue = currentDashboardTabs[tab.sourceName];
          const normalized = normalizeTabEntry(tabName, existingTabValue);
          tabId = normalized.id;
        } else {
          // Generate new id for new tabs
          tabId = generateUniqueUUID(new Set());
        }
      } else if (tab.sourceName && currentDashboardTabs?.[tab.sourceName]) {
        // Fallback to original tab data if no draft exists
        const existingTabValue = currentDashboardTabs[tab.sourceName];
        const normalized = normalizeTabEntry(tabName, existingTabValue);
        tabData = normalized.content;
        tabId = normalized.id;
      } else {
        // New tab with no data
        tabId = generateUniqueUUID(new Set());
      }

      tabsStructure[tabName] = {
        id: tabId,
        content: tabData ?? []
      };
    });

    const structurePayload = {
      [dashboardName]: tabsStructure
    } as Parameters<typeof mutate>[0];

    mutate(structurePayload);
  }, [currentDashboardTabs, dashboardName, draftTabs, getAllTabDrafts, mutate]);

  const allowTabReorder = Boolean(currentDashboardTabs);

  const tabsConfig = useMemo(() => {
    return draftTabs.map(tab => {
      const error = getDraftTabNameError(tab.name, tab.id);
      return {
        id: tab.id,
        name: tab.name,
        label: (
          <EditableTabLabel
            allowTabReorder={allowTabReorder}
            canDelete={draftTabs.length > 1}
            canMove={draftTabs.length > 1}
            error={error}
            name={tab.name}
            tabId={tab.id}
            onDelete={handleDeleteTab}
            onMoved={() => {
              handleDeleteTab(tab.id);
            }}
            onRename={handleRenameTab}
          />
        )
      };
    });
  }, [allowTabReorder, draftTabs, getDraftTabNameError, handleDeleteTab, handleRenameTab]);

  const handleDiscardClick = useCallback(() => {
    if (hasChanges) {
      setIsDiscardPopupOpen(true);
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose]);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();

  const handleAddAppClick = useCallback(() => {
    setIsEditMode(true);
    setEditingTarget(null);
    setDrawerStep(DrawerStep.SelectApp);
  }, [setDrawerStep, setEditingTarget, setIsEditMode]);

  const removeWidgetValue = useMemo(() => {
    return {
      openRemoveModal
    };
  }, [openRemoveModal]);

  const blocker = useBlocker(() => {
    return !isDeletingDashboard && hasChanges && !isSuccess && !isPending;
  });

  const handleBlockerConfirm = useCallback(() => {
    onClose();
    blocker.proceed?.();
  }, [blocker, onClose]);

  const handleBlockerCancel = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  return (
    <RemoveWidgetContext.Provider value={removeWidgetValue}>
      <Backdrop
        open={isDeletingDashboard}
        sx={{
          zIndex: 99999999
        }}
      >
        <CircularProgress />
      </Backdrop>
      <ConfirmationDialog
        open={isDiscardPopupOpen}
        {...presets.discard}
        onCancel={() => {
          setIsDiscardPopupOpen(false);
        }}
        onConfirm={() => {
          handleClose();
          setIsDiscardPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveWidgetPopupOpen}
        {...presets.removeWidget}
        onCancel={() => {
          setIsRemoveWidgetPopupOpen(false);
        }}
        onConfirm={() => {
          if (removeWidgetCallback) {
            removeWidgetCallback();
            setRemoveWidgetCallback(null);
          }
          setIsRemoveWidgetPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveAllPopupOpen}
        {...presets.removeAll}
        onCancel={() => {
          setIsRemoveAllPopupOpen(false);
        }}
        onConfirm={() => {
          handleRemoveAll();
          setIsRemoveAllPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveDashboardPopupOpen}
        {...presets.removeDashboard}
        onCancel={() => {
          setIsRemoveDashboardPopupOpen(false);
        }}
        onConfirm={() => {
          handleRemoveDashboard();
          setIsRemoveDashboardPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={blocker.state === 'blocked'}
        {...presets.discard}
        onCancel={handleBlockerCancel}
        onConfirm={handleBlockerConfirm}
      />
      <DashboardLayout
        disableTabRipple
        activeTab={draftActiveTabIndex}
        minTabWidth={50}
        renderRight={
          <EditActionButtons
            isPending={isPending || isDeletingDashboard}
            isSaveDisabled={isSaveDisabled}
            onAddApp={handleAddAppClick}
            onDiscard={handleDiscardClick}
            onRemoveAll={() => {
              setIsRemoveAllPopupOpen(true);
            }}
            onRemoveDashboard={() => {
              setIsRemoveDashboardPopupOpen(true);
            }}
            onSave={handleSave}
          />
        }
        tabNames={draftTabNames}
        tabsConfig={tabsConfig}
        title={<EditDashboardName onValidationChange={setHasDashboardNameErrors} />}
        onAddTab={handleAddTabDraft}
        onReorderTabs={allowTabReorder ? handleReorderTabs : undefined}
        onTabChange={handleDraftTabChange}
      >
        <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <EditBanner />
            <TilesOrder />
            <AppsOrder onTabCreated={handleAddTabWithId} />
          </Box>
        </PageInformationContext>
      </DashboardLayout>
    </RemoveWidgetContext.Provider>
  );
};
